import React, { useMemo } from "react";
import { KpiIcon } from "../../../DataManagment/Quantative/component/KpiIcons";
import MultiSelectDropdown from "../../../../globalComponents/form/dropdowns/MultiSelectDropdown";
import { buildStatusOptions } from "./assessmentFilters";

/**
 * Reusable filter bar for the assessment family of list pages (catalog +
 * assignment lists). Search + status (multi-select dropdown derived from
 * rows) + date range. `extraFilters` is an inline slot for page-specific
 * controls (owner-type, industry, etc.) — they sit on the same row as
 * the standard ones so the chrome stays consistent.
 *
 * The KPI cascade filter intentionally does NOT live here: assessments
 * don't carry tags — that filter belongs on the KPI picker modal.
 */
const AssessmentFilterBar = ({
  rows = [],
  statusField = "status_key",
  search = "",
  onSearchChange,
  searchPlaceholder = "Search…",
  statusFilter = [],
  onStatusFilterChange,
  statusPlaceholder = "Filter by status…",
  showStatus = true,
  dateRange = { from: "", to: "" },
  onDateRangeChange,
  dateLabel = "Date",
  extraFilters = null,
}) => {
  const statusOptions = useMemo(
    () => (showStatus ? buildStatusOptions(rows, statusField) : []),
    [rows, statusField, showStatus]
  );

  const hasAny =
    Boolean(search) ||
    (showStatus && statusFilter.length > 0) ||
    Boolean(dateRange?.from || dateRange?.to);

  const clearAll = () => {
    onSearchChange?.("");
    if (showStatus) onStatusFilterChange?.([]);
    onDateRangeChange?.({ from: "", to: "" });
  };

  const rowStyle = {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
    padding: "12px 16px",
  };

  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--rule)",
        borderRadius: 12,
        marginBottom: 12,
      }}
    >
      <div style={rowStyle}>
      <div
        style={{
          position: "relative",
          flex: "1 1 220px",
          minWidth: 180,
          maxWidth: 320,
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 11,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--ink-4)",
            pointerEvents: "none",
          }}
        >
          <KpiIcon name="search" size={14} />
        </div>
        <input
          className="kpi-input"
          style={{ paddingLeft: 34, paddingRight: search ? 32 : 12 }}
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => onSearchChange?.(e.target.value)}
          aria-label="Search"
        />
        {search ? (
          <button
            type="button"
            onClick={() => onSearchChange?.("")}
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: 18,
              color: "var(--ink-4)",
              lineHeight: 1,
              padding: 0,
            }}
            aria-label="Clear search"
          >
            ×
          </button>
        ) : null}
      </div>

      {showStatus ? (
        <div style={{ width: 220 }}>
          <MultiSelectDropdown
            options={statusOptions}
            selected={statusFilter}
            onChange={onStatusFilterChange}
            placeholder={statusPlaceholder}
            searchPlaceholder="Search status"
            showSelectAll
          />
        </div>
      ) : null}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          color: "var(--ink-4)",
          fontFamily: "var(--font-mono)",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontWeight: 700,
        }}
      >
        {dateLabel}
      </div>
      <input
        type="date"
        className="kpi-input"
        style={{ width: 150, height: 36, padding: "0 12px", lineHeight: "32px" }}
        value={dateRange?.from || ""}
        onChange={(e) =>
          onDateRangeChange?.({ ...dateRange, from: e.target.value })
        }
        aria-label={`${dateLabel} from`}
      />
      <span style={{ color: "var(--ink-4)" }}>→</span>
      <input
        type="date"
        className="kpi-input"
        style={{ width: 150, height: 36, padding: "0 12px", lineHeight: "32px" }}
        value={dateRange?.to || ""}
        onChange={(e) =>
          onDateRangeChange?.({ ...dateRange, to: e.target.value })
        }
        aria-label={`${dateLabel} to`}
      />

      {hasAny ? (
        <button
          type="button"
          onClick={clearAll}
          style={{
            marginLeft: "auto",
            padding: "0 12px",
            borderRadius: 8,
            background: "transparent",
            border: "1px solid var(--rule)",
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 700,
            color: "var(--ink-3)",
            height: 32,
          }}
        >
          Clear all
        </button>
      ) : null}
      </div>

      {extraFilters ? (
        <div
          style={{
            ...rowStyle,
            paddingTop: 0,
            borderTop: "1px solid var(--rule-2, #f1f5f9)",
            marginTop: 0,
            paddingTop: 12,
          }}
        >
          {extraFilters}
        </div>
      ) : null}
    </div>
  );
};

export default AssessmentFilterBar;
